import React from 'react';
import TextWidget from '@plone/volto/components/manage/Widgets/TextWidget';

/**
 * Tamanho recomendado para um título, em caracteres.
 *
 * O Google corta o título por largura em pixels (~600px no desktop), não por
 * contagem — mas 50 a 60 caracteres é a faixa que cabe em ~90% dos resultados,
 * e é a referência usada pelas ferramentas de SEO. No mobile sobra um pouco
 * mais (70–80), então 60 é um teto conservador que serve aos dois.
 */
export const SEO_TITLE_LENGTH = 60;

interface CharCountWidgetProps {
  id: string;
  value?: string;
  /** Teto sugerido. Não bloqueia a digitação — só avisa. */
  recommendedLength?: number;
  [key: string]: unknown;
}

/**
 * Campo de texto com contador de caracteres, no espírito do Twitter: o limite
 * é um conselho, não uma trava. Passar do teto deixa o contador vermelho, mas
 * o editor continua livre para escrever — há títulos que precisam ser longos.
 *
 * Por isso o `maxLength` do schema é deliberadamente ignorado aqui: o
 * `TextWidget` do Volto o repassa ao `<input>`, o que truncaria a digitação no
 * meio da palavra.
 */
const CharCountWidget: React.FC<CharCountWidgetProps> = (props) => {
  const { value, recommendedLength = SEO_TITLE_LENGTH } = props;
  const length = (value ?? '').length;
  const excedeu = length > recommendedLength;
  const perto = !excedeu && length > recommendedLength - 10;

  const cor = excedeu
    ? 'text-red-600 dark:text-red-400'
    : perto
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-gray-500 dark:text-night-400';

  return (
    <>
      <TextWidget {...props} maxLength={undefined} />
      <p className={`mt-1 text-right text-xs tabular-nums ${cor}`}>
        <span aria-hidden="true">
          {length}/{recommendedLength}
        </span>
        {/* Anunciar a cada tecla seria ruído; o leitor de tela só é avisado
            quando o texto passa do tamanho recomendado. */}
        <span role="status" className="sr-only">
          {excedeu
            ? `Título com ${length} caracteres, acima dos ${recommendedLength} recomendados.`
            : ''}
        </span>
      </p>
    </>
  );
};

export default CharCountWidget;
